import {
  DesktopDatePicker as MuiDesktopDatePicker,
  DesktopDatePickerProps as MuiDesktopDatePickerProps,
  LocalizationProvider,
} from '@mui/x-date-pickers-pro';
import {AdapterDayjs} from '@mui/x-date-pickers-pro/AdapterDayjs';
import {css} from '@emotion/css';
import {styled} from '@mui/material/styles';
import {inputBaseClasses} from '@mui/material';
import {useEffect, useMemo, useState} from 'react';
import Typography from '@mui/material/Typography';
import {prepareForSlot} from '@mui/base';

import {styleVariables} from 'core/constants';
import {dayjs, DateType, DATE_PICKER_FORMAT, formatDateToString, Dayjs} from 'utils/dateUtils';

import CalendarIcon from 'components/icons/calendar-old.svg';

const CalendarIconSlot = prepareForSlot(CalendarIcon);

export type DesktopDatePickerProps = Omit<
  MuiDesktopDatePickerProps<false>,
  'value' | 'minDate' | 'maxDate' | 'onChange'
> & {
  value: DateType | null;
  maxDate?: DateType | null;
  minDate?: DateType | null;
  minValidDate?: DateType | null;
  maxValidDate?: DateType | null;
  onChange: (value: DateType | null) => void;
  onBlur?: () => void;
  withTodayButton?: boolean;
  showValidation?: boolean;
  error?: boolean;
};

const SMuiDesktopDatePicker = styled((props: MuiDesktopDatePickerProps<false>) => <MuiDesktopDatePicker {...props} />)({
  [`& .${inputBaseClasses.input}`]: {
    textTransform: 'lowercase',
  },
});

export const sDesktopDatePicker = css`
  && {
    border-radius: 8px;

    & .PrivatePickersFadeTransitionGroup-root {
      font-size: 16px;
      font-weight: 600;
      line-height: 16px;
    }

    & .MuiTypography-caption {
      font-size: 12px;
      font-weight: 500;
      line-height: 20px;
    }

    & .MuiCalendarPicker-viewTransitionContainer {
      color: ${styleVariables.colorPalette.coolGrey(600)};
    }
  }
`;

const STodayContainer = styled('div')<{todayActive?: boolean}>`
  grid-column: 1 / 4;
  grid-row: 3;

  display: flex;
  justify-content: center;
  padding-bottom: 8px;
`;

const STodayButton = styled('div')<{todayActive?: boolean}>`
  padding: 2px 4px;
  cursor: pointer;
  user-select: none;
  text-align: center;
  border-radius: ${styleVariables.borderRadiusSmall}px;

  color: ${props => (props.todayActive ? styleVariables.colorWhite : styleVariables.colorPalette.elefontGrey(900))};
  background-color: ${props =>
    props.todayActive ? styleVariables.colorPalette.activeBlue(600) : styleVariables.colorPalette.elefontGrey(10)};
`;

const todayString = formatDateToString();

export type GetValidationErrorProps = {
  newDate: dayjs.ConfigType;
  minDateValidation: string | null | undefined;
  maxDateValidation: string | null | undefined;
};

export const getValidationError = ({newDate, minDateValidation, maxDateValidation}: GetValidationErrorProps) => {
  if (!dayjs(newDate).isValid()) {
    return 'Please enter a valid date';
  } else if (minDateValidation && dayjs(newDate).isBefore(minDateValidation)) {
    return `Date should be after ${dayjs(minDateValidation).format(DATE_PICKER_FORMAT)}`;
  } else if (maxDateValidation && dayjs(newDate).isAfter(maxDateValidation)) {
    return `Date should be before ${dayjs(maxDateValidation).format(DATE_PICKER_FORMAT)}`;
  }
};

export const DesktopDatePicker = ({
  value,
  minDate,
  maxDate,
  minValidDate: minDateValidation = minDate,
  maxValidDate: maxDateValidation = maxDate,
  onChange,
  format = DATE_PICKER_FORMAT,
  withTodayButton = false,
  showValidation,
  slots,
  slotProps,
  onBlur,
  error,
  ...props
}: DesktopDatePickerProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const dateValueMemo = useMemo(() => {
    return value !== null ? dayjs(formatDateToString(value)) : null;
  }, [value]);

  const [validationError, setValidationError] = useState(
    getValidationError({newDate: dayjs(value), minDateValidation, maxDateValidation})
  );

  useEffect(() => {
    setValidationError(
      getValidationError({
        newDate: dayjs(value),
        minDateValidation,
        maxDateValidation,
      })
    );
  }, [minDateValidation, maxDateValidation, value]);

  const handleTodayClick = () => {
    onChange(todayString);
    setIsOpen(false);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} dateLibInstance={dayjs}>
      <SMuiDesktopDatePicker
        open={isOpen}
        onOpen={() => setIsOpen(true)}
        onClose={() => setIsOpen(false)}
        enableAccessibleFieldDOMStructure={false}
        value={dateValueMemo}
        format={format}
        slots={{
          ...slots,
          openPickerIcon: CalendarIconSlot,
          actionBar: withTodayButton
            ? () => (
                <STodayContainer>
                  <STodayButton onClick={handleTodayClick}>
                    <Typography variant='subtitle3'>Today</Typography>
                  </STodayButton>
                </STodayContainer>
              )
            : undefined,
        }}
        slotProps={{
          ...slotProps,
          textField: {
            ...slotProps?.textField,
            ...(showValidation ? {error: Boolean(validationError), helperText: validationError} : {}),
            ...(error ? {error: true} : {}),
            onBlur,
          },
          desktopPaper: {
            ...slotProps?.desktopPaper,
            classes: {root: sDesktopDatePicker},
          },
          field: {
            ...slotProps?.field,
          },
          popper: {
            ...slotProps?.popper,
            placement: 'bottom-start',
            modifiers: [
              {
                name: 'offset',
                options: {
                  offset: [-12, 24],
                },
              },
            ],
          },
        }}
        showDaysOutsideCurrentMonth
        minDate={minDate ? dayjs(formatDateToString(minDate)) : undefined}
        maxDate={maxDate ? dayjs(formatDateToString(maxDate)) : undefined}
        onChange={(newDate: Dayjs | null) => {
          // Prevent setting wrong date because of the field input logic
          // with filling data from right to left with leading zeros
          const formattedValue = newDate && newDate.year() < 1000 ? dayjs(null) : newDate;

          if (!dayjs(formattedValue).isValid()) {
            onChange(formattedValue === null ? null : formattedValue.toString());
            return;
          }

          onChange(formatDateToString(formattedValue));
        }}
        {...props}
      />
    </LocalizationProvider>
  );
};
